<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Admin Header -->
    <div class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center gap-2">
        <Shield class="h-5 w-5 text-red-600" />
        <h1 class="text-xl font-bold text-red-800">Admin Panel</h1>
      </div>
      <p class="text-red-700 text-sm mt-1">Manage administrator privileges and user access.</p>
    </div>

    <!-- Tab Navigation -->
    <div class="flex space-x-1 bg-gray-100 p-1 rounded-lg">
      <button
        :class="[
          'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
          activeTab === 'admins'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        ]"
        @click="activeTab = 'admins'"
      >
        <Users class="h-4 w-4" />
        Admin Management
      </button>
      <button
        :class="[
          'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
          activeTab === 'testing'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        ]"
        @click="activeTab = 'testing'"
      >
        <TestTube class="h-4 w-4" />
        Test Users
      </button>
    </div>

    <!-- Admin Management Tab -->
    <div v-if="activeTab === 'admins'" class="space-y-6">
      <!-- Current Admins Section -->
      <Card class="p-6">
        <h2 class="text-lg font-semibold mb-4">Current Administrators</h2>
        <div v-if="loading.users" class="flex justify-center p-4">
          <Loader2 class="h-6 w-6 animate-spin" />
        </div>
        <div v-else class="space-y-3">
          <div v-if="currentAdmins.length === 0" class="text-center p-4 text-gray-500">
            No administrators found
          </div>
          <div
            v-for="admin in currentAdmins"
            :key="admin.id"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div>
              <h3 class="font-medium">
                {{ admin.name || 'Anonymous' }}
              </h3>
              <p v-if="admin.email" class="text-sm text-gray-600">
                {{ admin.email }}
              </p>
              <p v-if="admin.id === firstUserId" class="text-xs text-blue-600 font-medium">
                Original Admin
              </p>
            </div>
            <div class="flex items-center gap-2">
              <span
                v-if="admin.id === firstUserId"
                class="text-xs text-gray-500 px-2 py-1 bg-gray-200 rounded"
              >
                Protected
              </span>
              <Button
                v-else-if="currentAdmins.length > 1"
                variant="outline"
                size="small"
                class="text-red-600 border-red-300 hover:bg-red-50"
                @click="confirmDemoteAdmin(admin)"
              >
                Remove Admin
              </Button>
              <span v-else class="text-xs text-gray-500 px-2 py-1 bg-gray-200 rounded">
                Last Admin
              </span>
            </div>
          </div>
        </div>
      </Card>

      <!-- Promote User Section -->
      <Card class="p-6">
        <h2 class="text-lg font-semibold mb-4">Promote User to Admin</h2>
        <div class="space-y-4">
          <div>
            <label for="userSelect" class="block text-sm font-medium mb-2"> Select User </label>
            <Select id="userSelect" v-model="selectedUserId" placeholder="Choose a user...">
              <option v-for="user in nonAdminUsers" :key="user.id" :value="user.id">
                {{ user.name || 'Anonymous' }}{{ user.email ? ` (${user.email})` : '' }}
              </option>
            </Select>
          </div>
          <Button
            :disabled="!selectedUserId || promoting"
            variant="primary"
            class="w-full"
            @click="promoteUser"
          >
            <Loader2 v-if="promoting" class="h-4 w-4 mr-2 animate-spin" />
            {{ promoting ? 'Promoting...' : 'Promote to Admin' }}
          </Button>
        </div>
      </Card>

      <!-- Confirmation Modal -->
      <div
        v-if="showConfirmation"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      >
        <Card class="max-w-md w-full p-6">
          <h3 class="text-lg font-semibold mb-4">Confirm Action</h3>
          <p class="text-gray-600 mb-6">
            {{ confirmationMessage }}
          </p>
          <div class="flex gap-3">
            <Button variant="outline" class="flex-1" @click="showConfirmation = false">
              Cancel
            </Button>
            <Button
              variant="primary"
              class="flex-1"
              :disabled="executing"
              @click="executeConfirmedAction"
            >
              <Loader2 v-if="executing" class="h-4 w-4 mr-2 animate-spin" />
              {{ executing ? 'Processing...' : 'Confirm' }}
            </Button>
          </div>
        </Card>
      </div>

      <!-- Error/Success Messages -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
        <p class="text-red-800">
          {{ error }}
        </p>
      </div>
      <div v-if="success" class="bg-green-50 border border-green-200 rounded-lg p-4">
        <p class="text-green-800">
          {{ success }}
        </p>
      </div>
    </div>

    <!-- Test User Management Tab -->
    <div v-if="activeTab === 'testing'">
      <TestUserManager />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '../stores/user'
import { Shield, Loader2, Users, TestTube } from 'lucide-vue-next'
import Card from './ui/Card.vue'
import Button from './ui/Button.vue'
import Select from './ui/Select.vue'
import TestUserManager from './admin/TestUserManager.vue'

const userStore = useUserStore()

// State
const allUsers = ref([])
const selectedUserId = ref('')
const loading = ref({ admins: false, users: false })
const promoting = ref(false)
const demoting = ref(false)
const executing = ref(false)
const error = ref('')
const success = ref('')

// UI State
const activeTab = ref('admins')

// Confirmation modal
const showConfirmation = ref(false)
const confirmationMessage = ref('')
const pendingAction = ref(null)

// Computed
const currentAdmins = computed(() => allUsers.value.filter(user => user.is_admin))
const nonAdminUsers = computed(() => allUsers.value.filter(user => !user.is_admin))
const firstUserId = computed(() => {
  if (allUsers.value.length === 0) return null
  return [...allUsers.value].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]?.id
})

// Methods
async function loadUsers() {
  try {
    loading.value.users = true
    allUsers.value = await userStore.fetchAllUsersForAdmin()
  } catch (err) {
    error.value = 'Failed to load users'
    console.error(err)
  } finally {
    loading.value.users = false
  }
}

function confirmPromoteUser() {
  const user = allUsers.value.find(u => u.id === selectedUserId.value)
  if (!user) return

  confirmationMessage.value = `Are you sure you want to promote "${user.name || 'Anonymous'}" to administrator? They will have full access to admin features.`
  pendingAction.value = { type: 'promote', userId: selectedUserId.value }
  showConfirmation.value = true
}

function confirmDemoteAdmin(admin) {
  confirmationMessage.value = `Are you sure you want to remove administrator privileges from "${admin.name || 'Anonymous'}"?`
  pendingAction.value = { type: 'demote', userId: admin.id }
  showConfirmation.value = true
}

async function executeConfirmedAction() {
  if (!pendingAction.value) return

  executing.value = true
  try {
    if (pendingAction.value.type === 'promote') {
      await userStore.promoteToAdmin(pendingAction.value.userId)
      success.value = 'User promoted to admin successfully!'
      selectedUserId.value = ''
    } else if (pendingAction.value.type === 'demote') {
      await userStore.demoteAdmin(pendingAction.value.userId)
      success.value = 'Admin privileges removed successfully!'
    }

    await loadUsers()
    showConfirmation.value = false
    pendingAction.value = null

    // Clear success message after 3 seconds
    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    error.value = err.message || 'Action failed'
    console.error(err)
  } finally {
    executing.value = false
  }
}

async function promoteUser() {
  confirmPromoteUser()
}

// Auto-clear error messages
function clearError() {
  error.value = ''
}

// Lifecycle
onMounted(async () => {
  await loadUsers()
})
</script>
